/** Agent Settings screen (tenant/agent-settings.html, P6 group `build`). */
export const agentSettings = {
  title: "Agent settings · {{name}}",
  subtitle: "Control how the assistant talks, what it knows, when it hands off to staff, and what it must never do.",
  testInPlayground: "Test in Playground",
  more: "More actions",
  duplicate: "Duplicate agent",
  duplicated: "Duplicated as “{{name}}” in Draft",
  export: "Export settings (JSON)",
  exported: "Settings exported as {{file}}",
  history: "Version history",
  tabsLabel: "Settings sections",
  tabs: {
    general: "General",
    instructions: "Instructions",
    model: "Model & answers",
    retrieval: "Retrieval",
    knowledge: "Knowledge",
    guardrails: "Guardrails",
    escalation: "Escalation & handoff",
    languages: "Languages",
    glossary: "Glossary",
    memory: "Memory & privacy",
    danger: "Danger zone",
  },
  adv: "Adv",
  appliesTitle: "Applies to all channels",
  appliesBody: "Widget, web app, mobile app and Messenger use these settings. Channel-specific looks live in Widget Studio.",
  widgetStudio: "Widget Studio",
  saveBar: {
    saved: "All changes saved · {{time}}",
    savedNever: "All changes saved",
    dirty: "Unsaved changes",
    dirtyHint: "· changes apply to all channels when saved",
    discard: "Discard",
    save: "Save changes",
    savedToast: "Settings saved · live on all channels",
    discarded: "Changes discarded",
    reindex: "Retrieval changes need a re-index — your sources are being re-processed.",
    invalid: "Some fields need attention before saving.",
  },
  readOnly: "You can view these settings but not change them.",
  publishNeeded: "Going live needs the publish permission",
  greeting: {
    title: "Welcome message",
    hint: "The assistant's first message in every chat — widget, web app and mobile app. Your starter questions appear under it as chips.",
    label: "Welcome message",
    defaultNote: "Empty = default greeting: “{{preview}}”",
    save: "Save welcome message",
    saved: "Welcome message saved · live on all channels",
    savedDraft: "Saved to the widget draft. The widget has other unpublished changes — publish them from Widget Studio to go live.",
    startersNote: "Starter questions are edited in",
    studio: "Widget Studio",
    unavailable: "The welcome message can't be loaded here. You may not have access to Widget Studio.",
  },
  general: {
    identity: "Identity",
    identityHint: "How the assistant appears to the people it helps.",
    name: "Agent name",
    nameHint: "Shown in the chat header and in the inbox.",
    nameRequired: "Enter a name.",
    agentId: "Agent ID",
    agentIdHint: "Used in the widget snippet and the API.",
    copyId: "Copy agent ID",
    avatar: "Avatar",
    avatarHint: "Pick a preset. It appears in the chat header and on messages.",
    avatarPresets: { bot: "Bot icon", initials: "Initials", sparkles: "Sparkles icon", building: "Building icon", headset: "Headset icon" },
    description: "Description",
    descriptionHint: "Internal only — helps your team tell agents apart.",
    availability: "Availability",
    status: "Status",
    statusHint: "Paused agents show the offline message. Drafts are visible only in the Playground.",
    audience: "Audience",
    audienceHint: "Who can chat with this agent. Signed-in audiences are set up in Audiences & Access.",
    audienceLink: "Audiences & Access",
    audiencePublicHint: "Public visitors only see sources marked for the public audience.",
    audienceRequired: "Choose at least one audience.",
  },
  instructions: {
    role: "Role & persona",
    roleHint: "The system prompt. Written in plain language — no coding needed.",
    promptLabel: "System instructions",
    count: "{{count}} / {{max}} characters",
    variables: "Prompt variables",
    variablesHint: "· click to insert at cursor",
    variableEmpty: "empty until known",
    inserted: "Inserted {{variable}}",
    unknown: "Unknown variables: {{names}} — they will stay empty.",
    style: "Style",
    tone: "Tone",
    toneHint: "Pick up to three.",
    tones: { warm: "Warm", professional: "Professional", cheerful: "Cheerful", respectful: "Respectful (po/opo)", formal: "Formal", encouraging: "Encouraging" },
    length: "Answer length",
    lengthHint: "Medium ≈ 60–120 words. Lists of requirements are never cut short.",
    lengths: { short: "Short", medium: "Medium", detailed: "Detailed" },
  },
  model: {
    title: "AI model",
    hint: "Credits are deducted from your monthly message allowance.",
    usage: "Usage & credits",
    recommended: "Recommended",
    tier: { standard: "Standard", premium: "Premium" },
    credits_one: "{{count}} credit / msg",
    credits_other: "{{count}} credits / msg",
    notInPlan: "Not in your plan",
    noModels: "No models are available yet.",
    noModelsHint: "The AI providers are switched off for now. Check again later or contact support.",
    unavailable: "{{model}} is unavailable; using {{fallback}}.",
    unavailableUnknown: "The saved model",
    unavailableError: "That AI model is unavailable right now. Choose another model.",
    temperature: "Creativity (temperature)",
    precise: "Precise",
    creative: "Creative",
    maxTokens: "Max answer length (tokens)",
    maxTokensHint: "≈ {{words}} words. Long answers cost more and are rarely read on mobile.",
    maxTokensRange: "Between 100 and 4,000, in steps of 50.",
    quality: "Answer quality",
    strict: "Strict grounding — only answer from knowledge",
    strictHint: "The assistant will not use general knowledge. Recommended for prices, dates and policies.",
    citation: "Citation style",
    citationHint: "Answers stay free of numbers by default; the sources they came from are listed under each answer.",
    citations: { numbered: "Inline numbers [1] in the answer, plus the source list", source_names: "Sources below the answer (recommended)", hidden: "Hidden (staff can still see in Inbox)" },
    idk: "Show “I don't know” instead of guessing",
    idkHint: "When confidence is below the threshold, the fallback message is shown and the question is logged as a Knowledge Gap.",
    confidence: "Confidence threshold",
    answerMore: "Answer more often",
    onlySure: "Only when very sure",
    fallback: "Fallback message",
    followups: "Suggest follow-up questions",
    followupsHint: "Shows 2–3 tappable questions after each answer.",
  },
  retrieval: {
    info: "Defaults are tuned; change only if answers miss information.",
    infoBody: "After any change, run an evaluation to compare against your test set before going live.",
    chunking: "Chunking",
    chunkingHint_one: "How documents are split before embedding. Changing this re-indexes {{count}} source.",
    chunkingHint_other: "How documents are split before embedding. Changing this re-indexes {{count}} sources.",
    chunkSize: "Chunk size",
    chunkSizes: { "256": "256 tokens — short FAQs", "512": "512 tokens — recommended", "1024": "1024 tokens — long policies" },
    overlap: "Overlap",
    overlapHint: "Keeps sentences that cross chunk boundaries searchable.",
    search: "Search & ranking",
    searchHint: "Hybrid search (keyword + vectors) fused with Reciprocal Rank Fusion, then reranked.",
    topK: "Top-K candidates",
    topKHint: "Chunks fetched before reranking.",
    final: "Final context chunks",
    finalHint: "Chunks actually sent to the model.",
    hybrid: "Hybrid weighting",
    hybridValue: "{{keyword}} / {{meaning}} · RRF",
    keyword: "Keyword (exact terms)",
    meaning: "Meaning (paraphrases)",
    rerank: "Rerank results",
    rerankHint: "A cross-encoder re-scores candidates. Usually improves accuracy; adds a little latency.",
    rerankModel: "The reranker model is managed by the platform.",
    threshold: "Similarity threshold",
    thresholdHint: "Chunks below this score are dropped.",
    advanced: "Advanced",
    kDense: "Vector candidates",
    kLexical: "Keyword candidates",
    perSource: "Max chunks per source",
    perSourceHint: "Keeps one long document from crowding out the others.",
    neighbors: "Neighbor expansion",
    neighborsHint: "Adds the chunks right before and after a match for more context.",
    graph: "Knowledge graph",
    graphStage: "Experiment",
    graphHint: "Adds a second way to find related passages: entities and the links between them, extracted from your active knowledge. Answers still come only from your sources and the same audience rules.",
    understanding: "Query understanding",
    rewrite: "Query rewriting",
    rewriteHint: "Turns follow-ups like “how about for next year?” into full questions using the chat history.",
    contextual: "Contextual retrieval",
    contextualHint: "Adds a short document-context sentence to each chunk before embedding. Fewer missed answers on tables and lists.",
    reindex: "Re-index",
    multiQuery: "Multi-query expansion",
    multiQueryHint: "Searches several variations of the question (English, Filipino, keyword form). Helps Taglish; adds latency.",
    filters: "Metadata filters are applied automatically.",
    filtersBody: "Every search is filtered by the access level of the signed-in user, and can be narrowed by document type and effective date so superseded documents are ignored. Set these per source in the Knowledge Base.",
    knowledgeLink: "Knowledge Base",
    lastEval: "Last evaluation:",
    lastEvalValue: "{{date}} · {{name}} · {{pass}}/{{total}} passed",
    lastEvalNone: "No evaluation has run for this agent yet.",
    runEval: "Run evaluation with these settings",
  },
  guardrails: {
    blocked: "Blocked topics",
    blockedHint: "The assistant politely declines and redirects to topics it can help with.",
    blockedLabel: "Blocked topic",
    blockedPlaceholder: "Type a topic and press Enter",
    remove: "Remove {{name}}",
    removed: "Removed “{{name}}”",
    pii: "Personal data (Data Privacy Act 2012)",
    piiHint: "Detected values are masked in transcripts, analytics and model logs.",
    npc: "NPC-aligned",
    piiKinds: {
      mobile: "Mobile numbers",
      email: "Email addresses",
      national_id: "National ID / member numbers",
      address: "Home addresses",
      card_bank: "Card & bank numbers",
      gov_id: "Government IDs (TIN, SSS…)",
    },
    piiExamples: { mobile: "0917 ••• ••42", email: "r•••@gmail.com", national_id: "1072•••••••3", address: "Blk •• Lot ••, Brgy. •••", card_bank: "4111 •••• •••• 1111", gov_id: "123-•••-•••-000" },
    redact: "Redact {{name}}",
    safety: "Safety",
    injection: "Prompt-injection protection",
    injectionHint: "Ignores instructions hidden in messages or uploaded documents.",
    profanity: "Profanity filter",
    profanityHint: "English and Filipino profanity. Repeated abuse ends the chat and flags it in the Inbox.",
    policies: {
      no_guarantees: "Never promise outcomes or acceptance",
      no_guaranteesHint: "Blocks phrases that promise approval, acceptance or a guaranteed result.",
      no_medical_diagnosis: "No medical diagnosis",
      no_medical_diagnosisHint: "Declines to diagnose and offers to book an appointment instead.",
      no_legal_advice: "No legal advice",
      no_legal_adviceHint: "Declines legal interpretations and points to the right office.",
      no_financial_advice: "No financial advice",
      no_financial_adviceHint: "Declines investment or credit advice.",
    },
    rules: "Custom rules",
    rulesHint: "Checked on every answer before it is sent.",
    addRule: "Add rule",
    rule: "Rule {{n}}",
    moveUp: "Move rule {{n}} up",
    moveDown: "Move rule {{n}} down",
    removeRule: "Remove rule {{n}}",
    noRules: "No custom rules yet.",
  },
  escalation: {
    triggers: "Hand off to a person when…",
    triggersHint: "The conversation moves to the Inbox and staff are notified.",
    askHuman: "The user asks for a person",
    askHumanHint: "“Can I talk to someone?”, “pwede po ba makausap ang tao?”",
    lowConfidence: "Low confidence several times in a row",
    lowConfidenceHint: "Consecutive fallback answers before handing off.",
    strikes: "Fallback answers in a row",
    payment: "Payment, refund or balance topics",
    paymentHint: "Payments not reflected, refunds, disputed charges.",
    sentiment: "Negative sentiment",
    sentimentHint: "Frustration or complaints detected.",
    keywords: "Keywords",
    keywordsHint: "Comma-separated, e.g. emergency, complaint, reklamo, refund.",
    routing: "Who gets the handoff",
    routingHint: "A conversation handed to staff is routed in this order: payment team, a team whose topics match, then the default below.",
    routeTo: "Route handoffs to",
    routeTeam: "A team",
    routeTeamHint: "Anyone on the team can reply. With round-robin on, it goes to the next free member.",
    routePerson: "One person",
    routePersonHint: "Every handoff goes to the same member.",
    team: "Default team",
    teamHint: "Create teams and their topics under Team.",
    person: "Person",
    personHint: "Only active members who can reply in the Inbox can be chosen.",
    choosePerson: "Choose a member",
    personRequired: "Choose the person handoffs go to.",
    membersUnavailable: "Members couldn't load — you need access to Team to choose a person.",
    paymentTeam: "Payment team",
    paymentTeamHint: "Payment and refund topics go here before the default.",
    paymentTeamOff: "Turn on “Payment, refund or balance topics” above to use a payment team.",
    matchTopics: "Match team topics",
    matchTopicsHint: "A team whose routing topics match the conversation gets it first.",
    fallbackNote: "If no team or person is set (or they can't be reached), handoffs go to the Owner and Admins who can see this inbox.",
    sameAsDefault: "Same as the default",
    noTeam: "No team — Owner and Admins",
    teamsUnavailable: "Teams couldn't load — you need access to Team to change routing.",
    membersCount_one: "{{count}} member on this team.",
    membersCount_other: "{{count}} members on this team.",
    collect: "Ask for an email when the office is closed",
    collectHint: "The assistant asks for an email address after hours so the team can reach the visitor.",
    reminder: "Remind the team after (minutes)",
    reminderHint: "If nobody replies in {{minutes}} minutes the team and its lead get a reminder; after {{escalate}} minutes the Owner and Admins are told. Reminders only run in office hours.",
    reminderHintShort: "Enter whole minutes. The Owner and Admins are told after twice that.",
    reminderRange: "Enter a whole number from {{min}} to {{max}}.",
    hours: "Office hours",
    hoursHint: "{{timezone}}. Used for {{variable}} and handoff availability.",
    day: "Day",
    open: "Open",
    from: "From",
    to: "To",
    openLabel: "{{day}} open",
    fromLabel: "{{day}} opens at",
    toLabel: "{{day}} closes at",
    days: { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" },
    hoursInvalid: "Opening time must be before closing time.",
    outOfHours: "Out-of-hours message",
  },
  mandatory: {
    title: "Required handovers",
    hint: "From the industry pack you applied. These always go to a person: the assistant never resolves them, and no flow or tier can switch them off.",
    rulesScroll: "Required handovers, scrollable",
    tiersScroll: "Tier priorities, scrollable",
    rulesLabel: "Required handovers from the pack",
    colRule: "Rule",
    colDetected: "Detected by",
    colPriority: "Priority",
    colOutOfHours: "Out of hours",
    locked: "Locked: cannot be switched off",
    beforeTiers: "outranks every tier",
    trigger: {
      keywords: "Words",
      model_signal: "Words and the assistant's reading",
      person_request: "Asks for a person",
      low_confidence: "Low confidence in a row",
      negative_sentiment: "Negative sentiment",
    },
    priority: { urgent: "Urgent", high: "High", normal: "Normal", low: "Low" },
    outHelpline: "Helpline",
    outTicket: "Urgent ticket",
    outOnCall: "On-call alert",
    outNone: "Normal queue",
    neverChanges: "The assistant never changes an account (passwords, payout details, ID data, limits) and never carries out an exclusion request: it hands over to a person.",
    noRules: "The applied pack lists no required handovers.",
    emptyTitle: "No industry pack applied yet",
    emptyBody: "Apply an industry pack in Service settings to load its required handovers and tier priorities. You can still set who takes them below.",
    errorTitle: "Could not load the pack rules",
    errorBody: "The required handovers could not be read. Your settings below still apply.",
    retry: "Try again",
    tiersTitle: "Tiers and priority",
    tiersHint: "The tier comes only from what your system pushed about the signed-in customer. A top tier skips the menu and reaches the priority team's least-busy member; a required handover above always comes first.",
    tiersLabel: "Tier priorities from the pack",
    colTier: "Tier",
    colMenu: "Skip bot menu",
    colBasePriority: "Base priority",
    colPickup: "Target pickup",
    skipMenu: "Yes",
    noSkip: "No",
    minutes_one: "{{count}} min",
    minutes_other: "{{count}} min",
    team: "Team for required handovers",
    teamHint: "Where a required handover goes. Left empty, it uses the default route above.",
    onCall: "On-call contact",
    onCallHint: "Alerted at once when a required handover fires outside office hours.",
    noOnCall: "Nobody (the team is alerted as usual)",
    noOnCallWarning: "No on-call contact is set. Out of hours, a required handover still opens its urgent ticket, but nobody is alerted at once. Pick an on-call contact below.",
    helpline: "Helpline shown to the customer",
    helplineHint: "Shown in the reply to a required handover, for example the national helpline number. {{count}}/{{max}}",
    priorityTeam: "Priority team",
    priorityTeamHint: "A top-tier customer who asks for a person goes to this team's least-busy member.",
    noPriorityTeam: "No priority team (normal routing)",
  },
  languages: {
    title: "Languages the assistant understands",
    hint: "Pick at least one.",
    names: { en: "English", fil: "Filipino", taglish: "Taglish auto-detect", pam: "Kapampangan" },
    codes: { en: "EN", fil: "FIL", taglish: "TL", pam: "KAP" },
    descriptions: {
      en: "Primary language of most documents",
      fil: "Replies with po/opo when the user writes in Filipino",
      taglish: "Mixed English–Filipino questions are understood as one question",
      pam: "Understands simple questions; replies in English or Filipino",
    },
    beta: "Beta",
    required: "Keep at least one language on.",
    reply: "Reply in the user's language",
    replyHint: "If off, the assistant always replies in English.",
    noTranslate: "Your knowledge doesn't need translating.",
    noTranslateBody: "Documents stay as they are; questions in Filipino or Taglish are matched by meaning, and the answer is written in the user's language with the original source cited. Prices, dates and document names are never translated.",
  },
  memory: {
    title: "Conversation data",
    hint: "Covered by your Data Processing Agreement.",
    keep: "Keep conversations for",
    keepHint: "Transcripts older than this are deleted or anonymized every night.",
    workspaceDefault: "Workspace default",
    days: "{{count}} days",
    remember: "Remember signed-in users across sessions",
    rememberHint: "A signed-in user can continue last week's conversation. Anonymous visitors are never remembered beyond a few days.",
    anonymize: "Anonymize instead of delete after retention",
    anonymizeHint: "Names, numbers and emails are stripped; questions are kept for analytics and Knowledge Gaps.",
    dsr: "Data subject requests",
    dsrHint: "Export or erase everything about one person (by email or mobile).",
    dsrOpen: "New request",
  },
  danger: {
    title: "Danger zone",
    pause: "Pause agent",
    resume: "Resume agent",
    pauseHint: "All channels show the out-of-hours message. Nothing is deleted.",
    paused: "{{name}} paused",
    resumed: "{{name}} is live again",
    delete: "Delete agent",
    deleteHint: "Deletes the agent and stops its widget, Messenger page and chat link. Knowledge sources are kept.",
    deleteTitle: "Delete {{name}}?",
    deleteBody: "This cannot be undone. Conversations, leads and settings are deleted after the grace period in your retention policy.",
    deleteConfirm: "Type {{name}} to confirm",
    deleted: "Agent deleted",
  },
  versions: {
    title: "Version history",
    hint: "Published versions. Restoring loads that version's settings as the current configuration.",
    current: "Current",
    restore: "Restore",
    restored: "Restored version {{version}}",
    publish: "Publish current settings",
    published: "Published version {{version}}",
    empty: "No published versions yet",
    emptyBody: "Publish the agent to create the first version.",
    version: "Version {{version}}",
    by: "{{user}} · {{time}}",
    someone: "A team member",
    unpublished: "Saved",
  },
} as const;
